import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { DynamicComponent, HeaderBanner } from 'ctm-components';
import { pageContext, displayBooleanString } from 'ctm-util-functions';
import styled from 'styled-components';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import _ from 'lodash';
import { SummaryList } from '../summary_list/summary_list';
import { PageStyledWrapper } from '../products/page_styled_wrapper';
import { HeaderBannerText } from './common/heading_banner_text';
import { applicantFirstName } from '../../core/state_selectors';
import { ProductBox } from '../product_box/product_box';
import { StickyCTA } from './common/sticky_cta';

const ProductBoxWrapper = styled.div`
    margin-top: -10rem;
    display: flex;
    width: 100%;
    padding-right: 1.2rem;
    padding-left: 1.2rem;
    align-items: center;
    justify-content: center;
    max-width: 752px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 1rem;
`;

const SummaryListWrapper = styled.div`
    max-width: 752px;
    width: 100%;
`;

const ContentWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 20px;
    margin-top: -25px;
    @media only screen and (max-width: 600px) {
        padding-right: 16px;
        padding-left: 16px;
        padding-bottom: 40px;
    }
`;

const useStyles = createUseStyles(() => ({
    confirmationPage: {
        marginTop: '-2rem',
        overflowX: 'clip'
    }
}));

const _ConfirmationPageMain = ({
    firstName,
    snapshotCoverDetail,
    coverDetail,
    snapshotApplicant,
    applicant,
    sectionUpdated,
    setFooterDisclaimer,
    setShowStickyCTAFlag,
    env
}) => {
    const isXs = pageContext.getProps().pageState.breakpoint === 'xs';
    const classes = useStyles();
    const [hasSeenFooter, setHasSeenFooter] = useState(false);
    const [isSticky, setIsSticky] = useState(false);

    // navigationEntries will contain reload entries even if the verify page was refreshed
    // we need to only trap reloads made on the confirmation page
    const navigationEntries = window.performance.getEntriesByType('navigation');
    const confirmationReloads = navigationEntries.filter((nav) => {
        return nav.type === 'reload' && nav.name.includes('confirmation');
    });
    const browserHistory = pageContext.getProps().browserHistory;
    // if confirmation page is refreshed, redirect user to journey start
    if (confirmationReloads.length > 0) {
        browserHistory.push('/car-insurance/journey/start');
    }

    const intersectionObserverCallback = (entries) => {
        const [entry] = entries;

        if (entry.target.id === 'ad-main-bottom' && entry.isIntersecting) {
            setHasSeenFooter(true);
            setShowStickyCTAFlag();
            setIsSticky(false);
        }

        if (entry.target.id === 'ad-main-bottom' && !entry.isIntersecting && hasSeenFooter) {
            setIsSticky(true);
        }
    };

    const convertState = (newCoverDetail, newApplicant) => {
        if (!newCoverDetail) return {};
        const result = {
            factory_options: {
                factoryOptions: newCoverDetail.getIn(['vehicle', 'factoryOptions']),
                nonStandardAccessories: newCoverDetail.getIn(['vehicle', 'nonStandardAccessories']),
                modified: displayBooleanString(newCoverDetail.getIn(['vehicle', 'modified']))
            },
            car_make: {
                make: newCoverDetail.getIn(['vehicle', 'make']),
                year: newCoverDetail.getIn(['vehicle', 'year']),
                model: newCoverDetail.getIn(['vehicle', 'model']),
                body: newCoverDetail.getIn(['vehicle', 'body']),
                transmission: newCoverDetail.getIn(['vehicle', 'transmission']),
                fuel: newCoverDetail.getIn(['vehicle', 'fuel']),
                colour: newCoverDetail.getIn(['vehicle', 'colour'])
            },
            commencement_date: {
                commencementDate: newCoverDetail.getIn(['commencementDate'])
            },
            applicant_details: {
                contactNumber: newApplicant.getIn(['mobile']) || newApplicant.getIn(['phone']),
                firstName: newApplicant.getIn(['firstName']),
                lastName: newApplicant.getIn(['lastName']),
                email: newApplicant.getIn(['email'])
            },
            accident_hail_damage: {
                damaged: displayBooleanString(newCoverDetail.getIn(['vehicle', 'damaged'])),
                finance: newCoverDetail.getIn(['financeType']),
                currentlyInsured: displayBooleanString(newCoverDetail.getIn(['currentlyInsured'])),
                previousInsurer: newCoverDetail.getIn(['previousInsurer']),
                annualKilometres: newCoverDetail.getIn(['annualKilometres']),
                useType: newCoverDetail.getIn(['useType'])
            },
            driver_option: {
                driverOption: newCoverDetail.getIn(['driverOption'])
            },
            car_address: {
                buildingName: newCoverDetail.getIn(['overnightParking', 'address', 'buildingName']),
                unitType: newCoverDetail.getIn(['overnightParking', 'address', 'unitType']),
                unitNumber: newCoverDetail.getIn(['overnightParking', 'address', 'unitNumber']),
                streetNumber: newCoverDetail.getIn(['overnightParking', 'address', 'streetNumber']),
                streetName: newCoverDetail.getIn(['overnightParking', 'address', 'streetName']),
                suburb: newCoverDetail.getIn(['overnightParking', 'address', 'suburb']),
                ausState: newCoverDetail.getIn(['overnightParking', 'address', 'state']),
                overnightParkingPostcode: newCoverDetail.getIn(['overnightParking', 'address', 'postcode']),
                overnightParkingType: newCoverDetail.getIn(['overnightParking', 'parkingType'])
            },
            regular_driver_gender: {
                driverGender: newCoverDetail.getIn(['driver', 'gender']),
                driverDob: newCoverDetail.getIn(['driver', 'dob']),
                driverEmployment: newCoverDetail.getIn(['driver', 'employmentStatus']),
                licenceAge: newCoverDetail.getIn(['driver', 'licenceAge']),
                licenceType: newCoverDetail.getIn(['driver', 'licenceType']),
                comprehensiveInsured: newCoverDetail.getIn(['driver', 'comprehensiveInsured']),
                comprehensiveInsuredYears: newCoverDetail.get(['driver', 'comprehensiveInsuredYears']),
                driverPreviousClaims: newCoverDetail.getIn(['driver', 'anyPreviousClaims']) ?? null,
                driverClaimsDetails: newCoverDetail.getIn(['driver', 'claimsDetails']),
                driverOwnsAnotherCar: newCoverDetail.getIn(['ownsAnotherCar']),
                driverOwnsAnotherHome: displayBooleanString(newCoverDetail.getIn(['ownsHome']))
            },
            youngest_driver: {
                youngerDriverGender: newCoverDetail.getIn(['youngestDriver', 'gender']),
                youngerDriverDob: newCoverDetail.getIn(['youngestDriver', 'dob']),
                youngerDriverLicenceAge: newCoverDetail.getIn(['youngestDriver', 'licenceAge']),
                youngerDriverLicenceType: newCoverDetail.getIn(['youngestDriver', 'licenceType']),
                hasYoungerDriver: displayBooleanString(newCoverDetail.getIn(['hasYoungerDriver']))
            }
        };
        return result;
    };

    const highlightUpdate = () => {
        const snapshot = convertState(snapshotCoverDetail, snapshotApplicant);
        const updated = convertState(coverDetail, applicant);
        Object.keys(snapshot).forEach((key) => {
            const isEqual = _.isEqual(snapshot[key], updated[key]);
            sectionUpdated(key, !isEqual);
        });
    };

    const bannerElProps = {
        properties: {
            image: {
                alt: 'Compare The Market Australia Logo',
                src: '/evt/assets/brand/ctm/meerkat.png',
                title: 'Compare The Market Australia'
            },
            topMarginDesktop: '6rem',
            topMarginMobile: '4rem',
            bottomMarginDesktop: '5rem',
            bottomMarginMobile: '4rem',
            containerPaddingBottom: '11rem',
            containerMarginLeft: isXs ? '0' : '-0.5rem',
            innerContainerPaddingLeft: isXs ? '1.8rem' : '0',
            customHtml: () => {
                const subheadingHtml = () => {
                    return <div>Time for your renewal?</div>;
                };

                const titleHtml = () => {
                    return (
                        <div>
                            Your annual car insurance
                            <br />
                            {' '}
                            quote
                            <span style={{ fontWeight: 800 }}>&nbsp;- but Simples!</span>
                        </div>
                    );
                };

                const personalisedTextHtml = () => {
                    return (
                        <div>
                            Hi
                            {' '}
                            {firstName}
                            , review your details below. Please add or update any missing or outdated information.
                        </div>
                    );
                };

                return <HeaderBannerText subheadingHtml={subheadingHtml} headingHtml={titleHtml} personalisedTextHtml={personalisedTextHtml} isXs={isXs} />;
            },
            layout: 'row-reverse',
            layoutXs: 'column',
            backgroundColor: '#001464',
            fontColor: '#FFFFFF'
        }
    };

    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        const footerEl = document.getElementById('ad-main-bottom');
        const observer = new IntersectionObserver(intersectionObserverCallback, {
            root: null,
            rootMargin: '1px',
            threshold: 0.1
        });
        observer.observe(footerEl);

        // eslint-disable-next-line consistent-return
        return () => {
            observer.unobserve(footerEl);
        };
    }, [hasSeenFooter]);

    useEffect(() => {
        highlightUpdate();
        setFooterDisclaimer();
    }, []);

    return (
        <DynamicComponent
            component={(
                <PageStyledWrapper className={classNames('test-confirmationPage-main', classes.confirmationPage)}>
                    <HeaderBanner {...bannerElProps} />
                    <ContentWrapper>
                        <ProductBoxWrapper>
                            <ProductBox />
                        </ProductBoxWrapper>
                        <SummaryListWrapper>
                            <SummaryList />
                        </SummaryListWrapper>
                    </ContentWrapper>
                    <StickyCTA hasSeenFooter={hasSeenFooter} isSticky={isSticky} />
                </PageStyledWrapper>
            )}
        />
    );
};

_ConfirmationPageMain.propTypes = {
    firstName: PropTypes.string
};

const mapDispatchToProps = (dispatch) => ({
    updateAssumedQuestion: (questionList, questionId) => {
        dispatch({
            type: 'SET_VALUE',
            value: questionList.filter((question) => question !== questionId),
            stateKey: 'vertical.quote',
            identifier: 'assumedQuestions'
        });
    },
    sectionUpdated: (path, value) => {
        dispatch({
            type: 'SET_VALUE',
            identifier: `summaryPageEdit${path}`,
            stateKey: 'vertical.quote',
            value
        });
    },
    setFooterDisclaimer: () => {
        dispatch({
            type: 'SET_VALUE',
            identifier: 'helpers.isDisclaimerContentExpanded',
            value: false,
            stateKey: 'vertical.quote'
        });
    },
    setShowStickyCTAFlag: () => {
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: '11MonthsShowStickyCTA',
            value: true
        });
    }
});

const mapStateToProps = (state) => ({
    firstName: applicantFirstName({ state }),
    snapshotCoverDetail: state.getIn(['page', 'snapshotCoverDetail']),
    showStickyCTA: state.getIn(['page', '11MonthsShowStickyCTA']),
    snapshotApplicant: state.getIn(['page', 'snapshotApplicant']),
    coverDetail: state.getIn(['vertical', 'quote', 'form', 'coverDetail']),
    applicant: state.getIn(['vertical', 'quote', 'form', 'applicant']),
    env: state.getIn(['serverConfig', 'env'])
});

const ConfirmationPageMain = connect(
    mapStateToProps,
    mapDispatchToProps
)(_ConfirmationPageMain);

export { ConfirmationPageMain };
